// A lightweight storage framework: namespacing, JSON packing, optional TTL, optional validation

export interface StorageAdapter {
  get: (key: string) => string | null;
  set: (key: string, value: string) => void;
  remove: (key: string) => void;
}

interface PackedData<T> {
  v: T;
  e: number | null;
}

interface UnpackedData<T> {
  expired: boolean;
  value: T | null;
}

interface StoreOptions<T> {
  namespace: string;
  adapter: StorageAdapter;
  validate?: (value: T) => boolean;
}

interface Store<T> {
  get: (key: string) => T | null;
  set: (key: string, value: T, options?: { ttlMs?: number }) => void;
  remove: (key: string) => void;
}

const now = (): number => Date.now();

const pack = <T>(value: T, ttlMs?: number): string =>
  JSON.stringify({ v: value, e: ttlMs ? now() + ttlMs : null } as PackedData<T>);

const unpack = <T>(raw: string | null): UnpackedData<T> | null => {
  if (!raw) return null;
  try {
    const parsed = JSON.parse(raw) as PackedData<T>;
    if (parsed?.e && now() > parsed.e) return { expired: true, value: null };
    return { expired: false, value: parsed?.v ?? null };
  } catch {
    return null;
  }
};

export function createStore<T = any>({ namespace, adapter, validate }: StoreOptions<T>): Store<T> {
  const namespacedKey = (key: string): string => `${namespace}:${key}`;

  const get = (key: string): T | null => {
    const raw = adapter.get(namespacedKey(key));
    const data = unpack<T>(raw);
    if (!data) return null;
    if (data.expired) {
      adapter.remove(namespacedKey(key));
      return null;
    }
    const value = data.value;
    if (value === null) return null;
    if (typeof validate === 'function') {
      return validate(value) ? value : null;
    }
    return value;
  };

  const set = (key: string, value: T, { ttlMs }: { ttlMs?: number } = {}): void => {
    const payload = pack(value, ttlMs);
    adapter.set(namespacedKey(key), payload);
  };

  const remove = (key: string): void => {
    adapter.remove(namespacedKey(key));
  };

  return { get, set, remove };
} 